'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

interface UIContextType {
  leftPanelCollapsed: boolean
  setLeftPanelCollapsed: (v: boolean) => void
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: (v: boolean) => void
  globalLoading: boolean
  setGlobalLoading: (v: boolean) => void
}

const UIContext = createContext<UIContextType>({
  leftPanelCollapsed: false,
  setLeftPanelCollapsed: () => {},
  mobileSidebarOpen: false,
  setMobileSidebarOpen: () => {},
  globalLoading: false,
  setGlobalLoading: () => {},
})

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'

function getFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

function isBackendRequest(input: RequestInfo | URL): boolean {
  const rawUrl = getFetchUrl(input)
  if (!rawUrl) return false
  if (rawUrl.startsWith('/api/')) return true

  try {
    const requestUrl = new URL(rawUrl, window.location.origin)
    const apiUrl = new URL(API_BASE, window.location.origin)
    return requestUrl.origin === apiUrl.origin && requestUrl.pathname.startsWith(apiUrl.pathname)
  } catch {
    return false
  }
}

export function UIProvider({ children }: { children: React.ReactNode }) {
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [globalLoading, setGlobalLoading] = useState(false)
  const pathname = usePathname()
  const pendingRequestsRef = useRef(0)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pathnameRef = useRef(pathname)
  const isHomeRef = useRef(pathname === '/')

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
  }, [])

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const beginGlobalLoading = useCallback((delay = 160) => {
    if (isHomeRef.current) return
    clearHideTimer()
    if (showTimerRef.current) return
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null
      if (!isHomeRef.current) setGlobalLoading(true)
    }, delay)
  }, [clearHideTimer])

  const endGlobalLoading = useCallback(() => {
    clearShowTimer()
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null
      if (pendingRequestsRef.current <= 0) setGlobalLoading(false)
    }, 120)
  }, [clearHideTimer, clearShowTimer])

  useEffect(() => {
    pathnameRef.current = pathname
    isHomeRef.current = pathname === '/'

    if (pathname === '/') {
      pendingRequestsRef.current = 0
      clearShowTimer()
      clearHideTimer()
      setGlobalLoading(false)
      return
    }

    beginGlobalLoading(0)
    const timer = setTimeout(() => {
      if (pendingRequestsRef.current <= 0 && pathnameRef.current === pathname) {
        endGlobalLoading()
      }
    }, 420)

    return () => clearTimeout(timer)
  }, [beginGlobalLoading, clearHideTimer, clearShowTimer, endGlobalLoading, pathname])

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null
      if (!target) return
      const href = target.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return

      try {
        const nextUrl = new URL(href, window.location.origin)
        if (nextUrl.origin !== window.location.origin || nextUrl.pathname === '/') return
        if (nextUrl.pathname === window.location.pathname && nextUrl.search === window.location.search) return
        beginGlobalLoading(0)
      } catch {
        // noop
      }
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [beginGlobalLoading])

  useEffect(() => {
    const originalFetch = window.fetch

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const shouldTrack = !isHomeRef.current && isBackendRequest(input)

      if (shouldTrack) {
        pendingRequestsRef.current += 1
        beginGlobalLoading()
      }

      try {
        return await originalFetch(input, init)
      } finally {
        if (shouldTrack) {
          pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1)
          if (pendingRequestsRef.current === 0) endGlobalLoading()
        }
      }
    }

    return () => {
      window.fetch = originalFetch
      clearShowTimer()
      clearHideTimer()
    }
  }, [beginGlobalLoading, clearHideTimer, clearShowTimer, endGlobalLoading])

  return (
    <UIContext.Provider value={{ leftPanelCollapsed, setLeftPanelCollapsed, mobileSidebarOpen, setMobileSidebarOpen, globalLoading, setGlobalLoading }}>
      {children}
    </UIContext.Provider>
  )
}

export function useUI() {
  return useContext(UIContext)
}
